import { createContext, useContext, useState, useCallback } from 'react';
import { cn } from '../../lib/utils';

/**
 * Tabs - Reusable tab navigation component
 *
 * Usage:
 * <Tabs defaultValue="tab1">
 *   <TabsList>
 *     <TabsTrigger value="tab1">Tab 1</TabsTrigger>
 *     <TabsTrigger value="tab2">Tab 2</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="tab1">Content 1</TabsContent>
 *   <TabsContent value="tab2">Content 2</TabsContent>
 * </Tabs>
 */

const TabsContext = createContext(null);

function useTabs() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs provider');
  }
  return context;
}

export function Tabs({
  defaultValue,
  value: controlledValue,
  onValueChange,
  children,
  className,
}) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolledValue;

  const setValue = useCallback(
    (newValue) => {
      if (!isControlled) {
        setUncontrolledValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [isControlled, onValueChange]
  );

  return (
    <TabsContext.Provider value={{ value, setValue }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabsList({ children, className }) {
  return (
    <div
      role="tablist"
      className={cn(
        'flex items-center gap-1 p-1 bg-midnight-lighter/50 rounded-lg',
        'border border-cream/5',
        className
      )}
    >
      {children}
    </div>
  );
}

export function TabsTrigger({
  value,
  children,
  disabled = false,
  icon,
  className,
}) {
  const { value: selectedValue, setValue } = useTabs();
  const isSelected = selectedValue === value;

  return (
    <button
      role="tab"
      aria-selected={isSelected}
      aria-controls={`tabpanel-${value}`}
      disabled={disabled}
      onClick={() => setValue(value)}
      className={cn(
        'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium',
        'transition-all duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-midnight',
        isSelected
          ? 'bg-primary text-white shadow-sm'
          : 'text-cream/60 hover:text-cream hover:bg-cream/5',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {icon && <span className="w-4 h-4">{icon}</span>}
      {children}
    </button>
  );
}

export function TabsContent({
  value,
  children,
  className,
  forceMount = false,
}) {
  const { value: selectedValue } = useTabs();
  const isSelected = selectedValue === value;

  if (!forceMount && !isSelected) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${value}`}
      aria-labelledby={`tab-${value}`}
      hidden={!isSelected}
      className={cn(
        'focus:outline-none',
        isSelected && 'animate-fade-in',
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * Vertical Tabs variant for settings pages
 */
export function VerticalTabs({
  defaultValue,
  value: controlledValue,
  onValueChange,
  children,
  className,
}) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolledValue;

  const setValue = useCallback(
    (newValue) => {
      if (!isControlled) {
        setUncontrolledValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [isControlled, onValueChange]
  );

  return (
    <TabsContext.Provider value={{ value, setValue }}>
      <div className={cn('flex flex-col md:flex-row gap-6', className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export function VerticalTabsList({ children, className }) {
  return (
    <div
      role="tablist"
      aria-orientation="vertical"
      className={cn(
        'flex md:flex-col gap-1',
        'md:w-48 md:shrink-0 md:self-start',
        'overflow-x-auto md:overflow-visible',
        className
      )}
    >
      {children}
    </div>
  );
}

export function VerticalTabsTrigger({
  value,
  children,
  disabled = false,
  icon,
  description,
  className,
}) {
  const { value: selectedValue, setValue } = useTabs();
  const isSelected = selectedValue === value;

  return (
    <button
      role="tab"
      aria-selected={isSelected}
      aria-controls={`tabpanel-${value}`}
      disabled={disabled}
      onClick={() => setValue(value)}
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-lg text-left',
        'transition-all duration-200 whitespace-nowrap md:whitespace-normal',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-midnight',
        isSelected
          ? 'bg-primary/10 border border-primary/30 text-cream'
          : 'text-cream/60 hover:text-cream hover:bg-cream/5 border border-transparent',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {icon && (
        <span className={cn('w-5 h-5 shrink-0', isSelected ? 'text-primary' : 'text-cream/40')}>
          {icon}
        </span>
      )}
      <div className="min-w-0">
        <span className="font-medium">{children}</span>
        {description && (
          <p className="text-xs text-cream/40 mt-0.5 hidden md:block">{description}</p>
        )}
      </div>
    </button>
  );
}

export function VerticalTabsContent({ value, children, className }) {
  const { value: selectedValue } = useTabs();
  const isSelected = selectedValue === value;

  if (!isSelected) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${value}`}
      className={cn('flex-1 min-w-0 animate-fade-in', className)}
    >
      {children}
    </div>
  );
}

export default Tabs;
